import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import Page from "../../components/Page";
import { getCryoList, postCryoManualTemp } from "./api";

const TEMP_PRESETS = [
  { label: "4°C", value: "4" },
  { label: "6°C", value: "6" },
  { label: "-18°C", value: "-18" },
];

export default function CryoManualEntry() {
  const { id } = useParams(); // /frighi/manual/:id
  const [sp] = useSearchParams();
  const navigate = useNavigate();

  const macFromQs = sp.get("mac") || "";
  const frigoIdFromQs = sp.get("frigo_id") || "";
  const deviceIdFromQs = sp.get("device_id") || "";

  const [device, setDevice] = useState(null);

  const [temp, setTemp] = useState("");
  const [saving, setSaving] = useState(false);
  const [okMsg, setOkMsg] = useState("");
  const [errMsg, setErrMsg] = useState("");

  const frigoId = useMemo(() => {
    if (frigoIdFromQs) return Number(frigoIdFromQs);
    return id ? Number(id) : null;
  }, [id, frigoIdFromQs]);

  const deviceId = useMemo(() => {
    if (!deviceIdFromQs) return null;
    const n = Number(deviceIdFromQs);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [deviceIdFromQs]);

  useEffect(() => {
    let stop = false;

    (async () => {
      try {
        const rows = await getCryoList({ q: macFromQs || "", limit: 300 });
        if (stop) return;

        let found = null;

        if (frigoId) {
          found = rows.find((r) => Number(r.frigo_id || r.id) === Number(frigoId));
        }

        if (!found && deviceId) {
          found = rows.find((r) => Number(r.device_id) === Number(deviceId));
        }

        if (!found && macFromQs) {
          const norm = (s) => String(s || "").toLowerCase();
          found = rows.find(
            (r) => norm(r.mac ?? r.mac_address ?? r.macAddress) === norm(macFromQs)
          );
        }

        setDevice(found || null);
      } catch (e) {
        if (!stop) setErrMsg(e.message || String(e));
      }
    })();

    return () => {
      stop = true;
    };
  }, [frigoId, deviceId, macFromQs]);

  async function onSubmit(e) {
    e.preventDefault();
    setErrMsg("");
    setOkMsg("");

    try {
      setSaving(true);

      const mac = device?.mac ?? device?.mac_address ?? device?.macAddress ?? macFromQs;
      const tempToSend = String(temp).replace(",", ".");

      await postCryoManualTemp({
        frigo_id: device?.frigo_id ?? frigoId ?? null,
        dispositivo_id: device?.device_id ?? deviceId ?? null,
        mac_address: mac || "",
        temperatura: tempToSend,
      });

      setOkMsg("Temperatura registrata correttamente ✅");
      setTemp("");

      setTimeout(() => navigate("/frighi"), 800);
    } catch (e2) {
      setErrMsg(e2.message || String(e2));
    } finally {
      setSaving(false);
    }
  }

  const name =
    device?.nome ||
    device?.mac ||
    device?.mac_address ||
    device?.macAddress ||
    "Frigo";

  const normalizedTemp = String(temp).replace(",", ".");
  const selectedPreset = TEMP_PRESETS.find((p) => p.value === normalizedTemp);

  return (
    <Page title="Inserisci temperatura frigo" back>
      <div className="max-w-md mx-auto bg-white border rounded-2xl p-4 shadow">
        <div className="font-semibold text-lg mb-1">{name}</div>

        <div className="text-sm text-gray-500 mb-2">
          Inserisci la temperatura letta dal termometro e salva.
        </div>

        <div className="text-xs text-gray-400 mb-3">
          Frigo ID: {device?.frigo_id ?? frigoId ?? "—"}
          {" • "}
          Device ID: {device?.device_id ?? deviceId ?? "—"}
        </div>

        {errMsg && (
          <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-2">
            {errMsg}
          </div>
        )}

        {okMsg && (
          <div className="mb-3 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl p-2">
            {okMsg}
          </div>
        )}

        <form onSubmit={onSubmit} className="grid gap-3">
          <div>
            <label className="text-xs text-gray-500">Temperatura (°C)</label>

            <div className="mt-2 mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {TEMP_PRESETS.map((preset) => {
                const active = selectedPreset?.value === preset.value;
                return (
                  <button
                    key={preset.value}
                    type="button"
                    className={[
                      "rounded-xl border px-3 py-2 text-sm font-semibold transition",
                      active
                        ? "border-blue-600 bg-blue-600 text-white"
                        : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100",
                    ].join(" ")}
                    onClick={() => setTemp(preset.value)}
                  >
                    {preset.label}
                  </button>
                );
              })}

              <button
                type="button"
                className={[
                  "rounded-xl border px-3 py-2 text-sm font-semibold transition",
                  temp !== "" && !selectedPreset
                    ? "border-blue-600 bg-blue-600 text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
                ].join(" ")}
                onClick={() => setTemp("")}
              >
                Manuale
              </button>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                className="shrink-0 rounded-lg border px-3 py-2 text-lg font-semibold bg-gray-50 hover:bg-gray-100"
                onClick={() => {
                  if (!temp) {
                    setTemp("-");
                    return;
                  }
                  if (String(temp).startsWith("-")) {
                    setTemp(String(temp).slice(1));
                  } else {
                    setTemp(`-${temp}`);
                  }
                }}
              >
                ±
              </button>

              <input
                className="w-full border rounded-lg px-3 py-2 text-lg"
                type="text"
                inputMode="text"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                value={temp}
                onChange={(e) => {
                  const v = e.target.value.replace(/\s+/g, "");
                  if (/^-?\d*([.,]\d*)?$/.test(v) || v === "") {
                    setTemp(v);
                  }
                }}
                placeholder="Es. -22.5"
                required
              />
            </div>

            <div className="mt-1 text-xs text-gray-400">
              Per temperature sotto zero usa il pulsante ± se la tastiera non mostra il meno.
            </div>
          </div>

          <button
            className="w-full rounded-xl border px-3 py-2 disabled:opacity-60"
            disabled={saving}
            type="submit"
          >
            {saving ? "Salvo…" : "Salva temperatura"}
          </button>

          <div className="text-xs text-gray-500 text-center">
            Oppure <Link className="underline" to="/frighi">torna ai frighi</Link>
          </div>
        </form>
      </div>
    </Page>
  );
}
